'use client';

import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useSession, signOut as nextAuthSignOut } from 'next-auth/react';
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

import { useConversationUIStore } from '@/contexts/StoreContext';
import { logger } from '@/lib/logger';
import type { OrganizationFeature } from '@/lib/organization-features';
import { useOrganizationPolicyRefresh } from '@/hooks/useOrganizationPolicyRefresh';
import { parseAuthenticatedUser } from '@/lib/auth/parse-authenticated-user';
import { isSignInRequiredPath } from '@/lib/auth/public-paths';

import { buildUserFromSession } from './auth/build-user-from-session';
import { resolveSessionAction } from './auth/resolve-session-action';
import { useAuthSwitch } from './auth/useAuthSwitch';

import type { AuthenticatedUser, Organization, Workspace } from '@/lib/auth/types';
import type { UserInfo } from '@/types';
import type { ReactNode} from 'react';

import type { RefreshUserOutcome, SwitchWorkspaceOutcome } from './auth/outcomes';

// **既存の import 元を変えない。** この 2 つは呼び出し元が `@/components/AuthProvider` から
// 参照しているので、実装を切り出しても入口はここに残す（Issue #8078）。
export type { RefreshUserOutcome, SwitchWorkspaceOutcome } from './auth/outcomes';

interface AuthContextType {
  user: UserInfo | null;
  authenticatedUser: AuthenticatedUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /**
   * `/api/auth/me` を引き直してローカル状態へ反映する。
   *
   * 戻り値は**切り替え経路が「成功にしてよいか」を決めるため**にある（Issue #8076）。
   * 画面から呼ぶ分には無視してよい。
   */
  refreshUser: (shouldApply?: () => boolean) => Promise<RefreshUserOutcome>;
  applyOrganizationFeature: (organizationId: string, feature: OrganizationFeature, enabled: boolean) => void;
  switchOrganization: (organizationId: string) => Promise<void>;
  switchWorkspace: (workspaceId: string | null) => Promise<SwitchWorkspaceOutcome>;
  getAvailableOrganizations: () => Promise<Organization[]>;
  getAvailableWorkspaces: () => Promise<Workspace[]>;
  /**
   * **セッションが切り替え先へ動いている最中の行き先**（Issue #8129）。動いていなければ null。
   *
   * 切り替えは「セッション（JWT / DB の現在組織）を先に進めてから `/api/auth/me` を引き直す」
   * 順で走る。その間、画面は前の文脈のままだが、サーバはもう切り替え先で受け付ける。前の画面から
   * 書くと切り替え先へ入るので、画面側はこの値を見て前の中身を落とす。
   *
   * 引き直しだけ（切り替えを伴わない、失敗した切り替えの後始末など）では null のまま。
   * そのときは中身を落とさない ── 何も変わらないのにダイアログや入力を失わせない。
   */
  movingContext: AuthContextDestination | null;
}

/** 切り替え先。分かっている分だけ入る（組織だけの切り替えでは workspace は決まっていない）。 */
export interface AuthContextDestination {
  organizationId?: string | null;
  workspaceId?: string | null;
}


/**
 * `refreshUser` の例外を記録する（Issue #8076 レビュー指摘）。
 *
 * **握るが、消さない。** 戻り値を見ない呼び出し元もあるので、ここで記録しないと
 * `/api/auth/me` の失敗がどこにも残らない。
 */
function logRefreshUserFailure(error: unknown, userId: string | undefined): void {
  // **`Error` は文字列化せずそのまま渡す**（Issue #8076 レビュー指摘）。メッセージへ畳むと
  // `LegacyLogger` が例外として拾えず、スタックも cause も残らないうえ `captureToSentry` が
  // 走らない。握る例外を記録するために足した経路なのに、本番の監視へ届かなくなる。
  // **`metadata` で包まない**（Issue #8076 レビュー指摘）。`LegacyLogger.normalizeArgs` が
  // 第 3 引数のオブジェクトを `metadata` 配下へ入れるので、ここで包むと
  // `metadata.metadata.userId` になり、`metadata.userId` を見る検索やアラートから外れる。
  logger.error('[AuthProvider] failed to refresh user', error, {
    operation: 'refreshUser',
    endpoint: '/api/auth/me',
    userId,
  });
}




/**
 * `/api/auth/me` の応答から、受け入れてよい `AuthenticatedUser` だけを取り出す（Issue #8076）。
 *
 * **キャストしない。** この API には必須フィールドを欠いたフォールバック応答があり、
 * そのまま state へ入れると消費側（管理画面の `permissions` 参照）が落ちる。
 * 受け入れられないときは `null` を返し、呼び出し側が `fallbackNote` のとおりに倒す。
 */
async function readAuthenticatedUser(
  response: Response,
  fallbackNote: string,
): Promise<AuthenticatedUser | null> {
  const data: unknown = await response.json();
  const { user, droppedPermissionKeys } = parseAuthenticatedUser(
    typeof data === 'object' && data !== null ? (data as Record<string, unknown>).user : undefined,
  );
  if (!user) {
    logger.warn(`[AuthProvider] /api/auth/me returned an unusable user; ${fallbackNote}`);
  }
  // **落とした権限は境界で記録する**（Issue #8076 レビュー指摘）。検証側は副作用を持たない。
  if (droppedPermissionKeys.length > 0) {
    logger.warn('[AuthProvider] dropped non-boolean permission values', {
      operation: 'readAuthenticatedUser',
      endpoint: '/api/auth/me',
      // この分岐は検証を通った後なので `user` は必ず在り、`id` も検証済み
      // （`docs/logging-policy.md` は警告ログに `userId` を含める方針）。
      userId: user?.id,
      keys: droppedPermissionKeys,
    });
  }
  return user;
}

/**
 * 使えないセッションを片付けてサインインへ戻す（Issue #8078 で共通化）。
 *
 * **2 か所から呼ぶ。** `user.id` を持たない壊れたセッションと、`/api/auth/me` が 401 を
 * 返した期限切れ。どちらも「Cookie を消してからサインインへ」で、処理は同じだった。
 *
 * `signOut()` を通すのは Cookie を確実に消すため。素のリダイレクトだと壊れた Cookie が
 * 残り、サインインしてもまた同じ状態に戻る。**その `signOut` 自体が落ちたときだけ**
 * 直接リダイレクトへ倒す。
 */
function signOutBrokenSession(reason: string, callbackUrl = '/auth/signin'): void {
  logger.warn(`[AuthProvider] ${reason} - signing out`);
  void nextAuthSignOut({ callbackUrl }).catch((err: unknown) => {
    logger.error('[AuthProvider] signOut failed:', err);
    window.location.href = callbackUrl;
  });
}

/**
 * 保護された画面で未ログインになったら、戻り先付きでサインインへ送るか（Issue #8734）。
 *
 * middleware は Cookie の JWT を読むだけで、失効（`auth_epoch` の不一致など）は見ない。
 * 失効したセッションでも画面まで通り、`/api/auth/session` が `{}` を返すので、`user` を
 * 待つ画面が読み込み中のまま止まっていた。公開ページ（middleware と同じ一覧）は送らない。
 */
function signInUrlIfProtectedPage(status: string): string | null {
  if (status !== 'unauthenticated' || typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  if (!isSignInRequiredPath(pathname)) return null;
  return `/auth/signin?callbackUrl=${encodeURIComponent(`${pathname}${search}`)}`;
}

/**
 * セッションが本当に無いかを取り直して確かめる（Issue #8734）。
 *
 * NextAuth は `/api/auth/session` の取得に失敗しても `unauthenticated` にする。一時的な
 * 通信の失敗で有効なセッションを消さないよう、200 で空（`{}`）が返ったときだけ `gone`。
 * 中身があれば `present`、取り直しにも失敗したら `unknown`。返ってこないときは 10 秒で打ち切る。
 */
async function confirmSession(): Promise<'gone' | 'present' | 'unknown'> {
  try {
    const response = await fetch('/api/auth/session', { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
    if (response.status !== 200) return 'unknown';
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) return 'unknown';
    return Object.keys(body).length === 0 ? 'gone' : 'present';
  } catch {
    return 'unknown';
  }
}

/**
 * 初回ロードの `/api/auth/me` を読む（Issue #8078）。
 *
 * 3 つを区別して返す。**state を触らない**ので、呼び出し元が片付け方を決められる。
 *
 * - `AuthenticatedUser` — 受け入れられた
 * - `'expired'` — 401。セッションが使えないので片付けが要る
 * - `null` — 引けなかった / 受け入れられなかった。セッションから組み立てる
 */
async function readInitialUserResponse(): Promise<AuthenticatedUser | 'expired' | null> {
  try {
    const response = await fetch('/api/auth/me');
    if (response.status === 401) return 'expired';
    if (!response.ok) return null;
    return await readAuthenticatedUser(response, 'falling back to session');
  } catch (error) {
    // API 失敗時はセッションからフォールバック
    logger.warn('Failed to fetch full user info, using session fallback:', error);
    return null;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status, update } = useSession();
  const queryClient = useQueryClient();
  // Issue #4139: WS / Org 切替時に conversation 一覧キャッシュをクリアするため Valtio store を参照
  const { actions: conversationUIActions } = useConversationUIStore();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(null);
  const [loadingCount, setLoadingCount] = useState(0); // Start with 0 since NextAuth handles initial load
  /** 切り替え先へ動いている最中の行き先（Issue #8129）。`markContextMove` が立てる。 */
  const [movingContext, setMovingContext] = useState<AuthContextDestination | null>(null);
  const loading = status === 'loading'
    || loadingCount > 0
    || (status === 'authenticated' && authenticatedUser === null);

  // signOut 連打を防ぐため useRef で状態管理（複数箇所で使用）
  const signingOutRef = useRef(false);
  /**
   * **走っている切り替えの全部**。重なったとき、どちらが先に終わっても、まだ動いている方が
   * 残っている限り印を外さないため（レビュー指摘 P1）。
   */
  const movingContextsRef = useRef<{ token: symbol; destination: AuthContextDestination }[]>([]);


  // Convert AuthenticatedUser to UserInfo for backward compatibility
  const convertToUserInfo = useCallback((authUser: AuthenticatedUser): UserInfo => {
    // Issue #449: null状態を適切に処理
    return {
      sub: authUser.id,
      email: authUser.email,
      name: authUser.name || authUser.email,
      organizations: authUser.organization ? [authUser.organization] : [],
      workspaces: authUser.workspace ? [authUser.workspace] : [],
      organizationMemberships: authUser.organizationMembership ? [authUser.organizationMembership] : []
    };
  }, []);

  // NextAuth sessionからユーザー情報を同期
  // Issue #1637: 初回ロード時に /api/auth/me を呼び出してワークスペース情報を取得
  // 依存配列は session?.user?.id のみにして、セッション変更による過剰な API 呼び出しを防ぐ
  const sessionUserId = session?.user?.id;
  // ログ用。`refreshUser` は `useCallback` の依存を増やしたくないので ref 経由で読む。
  //
  // **render 中に書かない**（Issue #8076 レビュー指摘）。破棄された render の代入が、
  // 既に commit 済みの非同期 callback から読まれる ref を上書きし、確定していない
  // ユーザー ID がエラーログに載り得る。
  const sessionUserIdRef = useRef<string | undefined>(sessionUserId);
  useEffect(() => {
    sessionUserIdRef.current = sessionUserId;
  }, [sessionUserId]);
  /** 認証状態を空にする。「未認証」「壊れたセッション」「id が無い」で共通。 */
  const clearAuthState = useCallback((): void => {
    setAuthenticatedUser(null);
    setUser(null);
  }, []);

  /**
   * 期限切れセッションを片付ける。**一度だけ**（`signingOutRef`）。
   *
   * 初回ロードと effect の両方から同じ状態へ落ちるので、`signOut` が二重に走らないよう
   * ref で押さえる。
   */
  const clearForExpiredSession = useCallback((): void => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    clearAuthState();
    signOutBrokenSession('Session expired or invalid');
  }, [clearAuthState]);

  /**
   * `/api/auth/me` を引き、受け入れられなければセッションから組み立てる。
   *
   * `'signed-out'` は「セッションが使えないので片付けた」。呼び出し元は状態を触らない
   * （サインインへ遷移するので、書いても見えないうえに破棄された state を触る）。
   */
  const fetchInitialUser = useCallback(async (): Promise<AuthenticatedUser | 'signed-out'> => {
    if (!session?.user) return 'signed-out';
    const fetched = await readInitialUserResponse();
    if (fetched === 'expired') {
      clearForExpiredSession();
      return 'signed-out';
    }
    // **セッションから組み立てる**（Issue #8078 で `buildUserFromSession` へ切り出し）。
    // `/api/auth/me` が引けない、または検証に通らなかった場合の受け皿。
    return fetched ?? buildUserFromSession(session);
  }, [session, clearForExpiredSession]);

  /**
   * 初回ロードの取得（Issue #1637）。**切り替えの `refreshUser` とは別**。
   *
   * セッションには workspace 情報が無いので `/api/auth/me` から引く。引けない・受け入れ
   * られない応答のときはセッションから組み立てる（`buildUserFromSession`）。
   *
   * effect の中に置いていたのを外へ出した（Issue #8078）。effect が 165 行・複雑度 40 に
   * なっていて、**「いつ走るか」と「何をするか」が 1 つの関数に混ざっていた**。
   */
  const loadInitialUser = useCallback(async (): Promise<void> => {
    if (!session?.user) return;
    setLoadingCount((prev) => prev + 1);
    try {
      const resolved = await fetchInitialUser();
      // 401 は「片付けて戻る」だけで、状態の反映はしない。
      if (resolved === 'signed-out') return;
      setAuthenticatedUser(resolved);
      setUser(convertToUserInfo(resolved));
    } finally {
      setLoadingCount((prev) => Math.max(0, prev - 1));
    }
  }, [session, convertToUserInfo, fetchInitialUser]);


  useEffect(() => {
    // **行き先は純粋関数が決める**（Issue #8078）。effect には「決めた結果を実行する」
    // だけを残す ── 以前は 1 つの effect にクリア・壊れたセッションの片付け・初回ロードが
    // 入れ子で混ざっていて、どの条件でどれが走るのか追えなかった。
    switch (resolveSessionAction(status, !!sessionUserId)) {
      case 'clear':
        clearAuthState();
        return;
      case 'sign-out-corrupted':
        if (signingOutRef.current) return;
        signingOutRef.current = true;
        clearAuthState();
        signOutBrokenSession('Session is corrupted (no user.id)');
        return;
      case 'load':
        // Issue #1637: セッション確立後に /api/auth/me から完全なユーザー情報を取得。
        // workspace 情報は NextAuth セッションに含まれないため API から取得する必要がある。
        // ワークスペース切り替え時は refreshUser() が呼ばれるので、ここは初回ロード用。
        void loadInitialUser();
        return;
      case 'wait':
        return;
    }
    // **依存は `sessionUserId` と `status` だけにする**（意図的）。
    //
    // `session` を入れると、NextAuth が同じ内容で新しいオブジェクトを返すたびに
    // 初回ロードが走り直す。`loadInitialUser` / `clearAuthState` も `session` を
    // 閉じ込めているので同じ理由で外す。**「セッションの主体か状態が変わったときだけ
    // 走る」**のがこの effect の意図で、それは 2 つで表せる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUserId, status]);

  // 保護された画面で未ログインなら、戻り先付きでサインインへ送る（Issue #8734）。
  // 公開ページから画面内のリンクで保護された画面へ移ったときも判定し直すよう、パスの変化でも走らせる
  const pathname = usePathname();
  // 確かめている間にログインし直されたら（別のタブなど）、古い結果で新しいセッションを消さないため
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: パスの変化で判定し直すため pathname を依存に入れる（本体は window.location を読む）
  useEffect(() => {
    const signInUrl = signInUrlIfProtectedPage(status);
    if (!signInUrl || signingOutRef.current) return;
    signingOutRef.current = true;
    void confirmSession().then((result) => {
      // 確かめ終わった時点の状態と画面で判定し直す。公開ページへ移っていたら何もせず、戻り先も今の画面で作る
      const currentSignInUrl = signInUrlIfProtectedPage(statusRef.current);
      if (result === 'gone' && currentSignInUrl) {
        signOutBrokenSession('Session is revoked on a protected page', currentSignInUrl);
        return;
      }
      // セッションがあった・確かめられなかったときは何もしない（この PR の前と同じ）。
      // 読み込み直しは編集中の内容を消しうるので行わない。NextAuth はタブに戻ったときに取り直す
      signingOutRef.current = false;
    });
  }, [status, pathname]);

  // ログイン成功時にサインアウト中フラグをリセット
  useEffect(() => {
    if (status === 'authenticated' && session?.user?.id) {
      signingOutRef.current = false;
    }
  }, [status, session?.user?.id]);

  /**
   * `/api/auth/me` を引き直してローカル状態へ反映する。
   *
   * `shouldApply` は **`fetch` の応答が返ったあと、setter を呼ぶ直前**に評価する
   * （Issue #8030 レビュー指摘）。`await refreshUser()` の**後**で判定しても、
   * 書き込みはもう済んでいるので遅い。ワークスペース切り替えが追い越されたとき、
   * 古い断面で `authenticatedUser` / `user` を上書きさせないために要る。
   */
  const refreshUser = useCallback(async (shouldApply?: () => boolean): Promise<RefreshUserOutcome> => {
    // NextAuthのセッション更新はrefetchで処理
    // カスタムエンドポイントからの情報取得が必要な場合はここに追加
    setLoadingCount(prev => prev + 1);

    try {
      const response = await fetch('/api/auth/me');
      // **検証に通らなければ更新しない**（Issue #8076）。古い値を保つ方が、
      // 壊れた値で上書きするより安全（消費側は `permissions` を素で読む）。
      const authUser = response.ok
        ? await readAuthenticatedUser(response, 'keeping the current state')
        : null;
      if (!authUser) return 'unusable';
      if (shouldApply && !shouldApply()) return 'skipped';
      setAuthenticatedUser(authUser);
      setUser(convertToUserInfo(authUser));
      return 'applied';
    } catch (error) {
      logRefreshUserFailure(error, sessionUserIdRef.current);
      return 'unusable';
    } finally {
      setLoadingCount(prev => prev - 1);
    }
  }, [convertToUserInfo]);

  // 保存済みの値は auth/me の一時的な失敗でも失わない。切替後の別組織には適用しない。
  const applyOrganizationFeature = useCallback((organizationId: string, feature: OrganizationFeature, enabled: boolean) => {
    setAuthenticatedUser(current => {
      if (current?.currentOrganizationId !== organizationId || current.organization?.id !== organizationId) return current;
      return { ...current, organization: { ...current.organization, settings: { ...current.organization.settings, [feature]: enabled } } };
    });
  }, []);

  useOrganizationPolicyRefresh(authenticatedUser, setAuthenticatedUser);

  // 切り替えは `useAuthSwitch` が持つ（Issue #8078）。ここは Context の構築と表示境界に絞る。
  /**
   * 切り替えの開始と終わりを受け取る（Issue #8129）。
   *
   * **重なることがある**（切り替え中に別の切り替えが始まる）。走っているものを全部覚えて、
   * **1 つでも残っている間は印を立てたまま**にする。表示する行き先は、残っているうちで
   * 最後に始まったもの。
   *
   * 最後の 1 本だけを覚えると、**先に終わった方がまだ動いている方の印まで消す**
   * （レビュー指摘 P1）。組織切り替えには追い越しの取り消しが無いので、B が先に終わっても
   * A はそのまま DB を A の組織へ進められる。印が消えたままそれが起きると、画面は B の
   * 中身に戻っているのにサーバだけ A へ動き、そこから書くと A へ入る。
   */
  const markContextMove = useCallback((destination: AuthContextDestination) => {
    const token = Symbol('context-move');
    movingContextsRef.current = [...movingContextsRef.current, { token, destination }];
    setMovingContext(destination);

    return () => {
      movingContextsRef.current = movingContextsRef.current.filter(move => move.token !== token);
      const remaining = movingContextsRef.current.at(-1);
      setMovingContext(remaining ? remaining.destination : null);
    };
  }, []);

  const { switchOrganization, switchWorkspace } = useAuthSwitch({
    refreshUser,
    update,
    queryClient,
    conversationUIActions,
    markContextMove,
  });

  const handleSignOut = useCallback(async () => {
    try {
      // 自分で出ていく途中の未ログインを、失効と取り違えてサインインへ送らない（#8734）
      signingOutRef.current = true;
      // ローカル状態を即座にクリア
      setAuthenticatedUser(null);
      setUser(null);

      // NextAuthでセッション削除後、/auth/signout にリダイレクト
      // 注意: NextAuthは同一オリジンのURLのみ許可するため、
      // Cognitoログアウト URL（外部URL）は callbackUrl に指定できない
      // /auth/signout ページで2段階目のCognitoログアウトリダイレクトを実行
      await nextAuthSignOut({
        callbackUrl: '/auth/signout',
        redirect: true
      });

      // この後のコードは実行されない（リダイレクトが発生するため）
    } catch (err) {
      // エラー時のみここが実行される
      logger.error('サインアウトエラー:', err);
      // エラー時は常にサインインページへリダイレクト
      window.location.href = '/auth/signin';
    }
  }, []);


  const getAvailableOrganizations = useCallback(async (): Promise<Organization[]> => {
    try {
      const response = await fetch('/api/auth/organizations');
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      const data = await response.json();
      return data.organizations || [];
    } catch (_error) {
      return [];
    }
  }, []);

  const getAvailableWorkspaces = useCallback(async (): Promise<Workspace[]> => {
    try {
      const response = await fetch('/api/auth/workspaces');
      if (!response.ok) {
        throw new Error('Failed to fetch workspaces');
      }
      const data = await response.json();
      return data.workspaces || [];
    } catch (_error) {
      return [];
    }
  }, []);

  // NextAuthがセッション管理を担当するため、初期チェックは不要

  return (
    <AuthContext.Provider value={{
      user,
      authenticatedUser,
      loading,
      signOut: handleSignOut,
      refreshUser,
      applyOrganizationFeature,
      switchOrganization,
      switchWorkspace,
      getAvailableOrganizations,
      getAvailableWorkspaces,
      movingContext
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
